/**
 * Панель источника на вкладке «Мониторинг» (решение Ильи 15.10.2026, миграция 0021): здоровье,
 * ВЕС и ВКЛЮЧЁННОСТЬ одного источника. Открывается нажатием на его ячейку в «Живости
 * источников».
 *
 * РЕЕСТР — УМОЛЧАНИЕ, ПРАВКА ЗДЕСЬ — ОТКЛОНЕНИЕ. Рядом с каждой ручкой стоит, что сказал реестр
 * (`config/sources/<brand>.yaml`), и кнопка «Сбросить к реестру»: она отправляет `null`, то
 * есть снимает отклонение, а не закрепляет реестровое число — иначе правка файла перестала бы
 * что-либо менять (тот же приём, что «(дефолт)» у модели писателя). Действующие значения
 * считает бэкенд; экран показывает их и не выводит сам.
 *
 * «СОХРАНИТЬ» ШЛЁТ ТОЛЬКО ИЗМЕНЁННОЕ одним `PATCH`: отсутствующее поле бэкенд не трогает, и
 * повторная запись нетронутого веса оставила бы в журнале «изменил вес», которого не было.
 * Оптимистичного показа нет: после ответа панель берёт строку из ответа, а экран перечитывает
 * список (`onSaved`). Отказ — строкой в панели, набранное не сбрасывается.
 *
 * ОПТИМИСТИЧНАЯ БЛОКИРОВКА. Тело несёт `expected_changed_at` — момент последней правки, который
 * видел человек. Источник успели изменить в другой вкладке — 409 `source_override_conflict`:
 * панель перечитывает строку, показывает «источник успели изменить — данные обновлены» и
 * сбрасывает ручки на свежие значения. Молча перезаписать чужую правку значило бы потерять её.
 *
 * БАЗА СЛЕДУЕТ ЗА ЭКРАНОМ, пока у человека нет несохранённого: строка из перечитанного списка
 * НОВЕЕ базы (позже `changed_at` или другие действующие значения — например, после выкладки
 * реестра) заменяет её. Старее — нет: список, перечитанный до ответа PATCH, не откатывает
 * панель на прошлое.
 *
 * ШАПКА ПОКАЗЫВАЕТ СОХРАНЁННОЕ, а несохранённое помечено отдельно: «обходится · вес 0.8» и
 * ползунок на 0.5 рядом читались бы как два разных ответа на один вопрос.
 *
 * ВКЛЮЧЕНИЕ ВЫКЛЮЧЕННОГО РЕЕСТРОМ РАЗРЕШЕНО, но не молча: у такого источника в реестре есть
 * причина (комментарий над `enabled: false`), и человек видит её до того, как нажмёт. Источник
 * выключали за дело чаще, чем по недосмотру.
 *
 * Действует со следующего прогона мониторинга: сервис читает отклонения на открытии прогона,
 * идущий обход их не видит.
 */

import {
  canonicalSourceWeight,
  SOURCE_WEIGHT_MAX,
  SOURCE_WEIGHT_MIN,
  SOURCE_WEIGHT_STEP,
  type SourceControl,
  SourceControlSchema,
  SourceControlsResponseSchema,
  type SourceOverridePatchBody,
  type SourceYield,
} from '@smm/contracts/schemas';
import { type JSX, type ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { ApiRequestError, get, patch } from '../api/client.ts';
import { shortMsk } from '../components/format.ts';
import { Modal } from '../components/Modal.tsx';
import styles from './SourcePanel.module.css';

/** «0.8», «0.35», «1.0» — вес как в реестре: точка, без хвоста двоичной дроби. */
export function weightLabel(w: number): string {
  const fixed = canonicalSourceWeight(w).toFixed(2);
  return fixed.endsWith('0') ? fixed.slice(0, -1) : fixed;
}

const errorText = (e: unknown): string =>
  e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);

/**
 * Воронка выхода одной строкой (ревью 18.10.2026, №3): здоровье говорит, отвечает ли лента,
 * а эта строка — доходит ли от неё что-нибудь до материала.
 */
export function yieldText(y: SourceYield): string {
  if (y.runs === 0) return `за ${String(y.window_days)} сут обходов не было`;
  const articles =
    y.articles_tried === 0
      ? 'статей не качали'
      : `статей скачано ${String(y.articles_ok)} из ${String(y.articles_tried)}` +
        (y.articles_blocked > 0 ? ` (заблокировано ${String(y.articles_blocked)})` : '');
  return (
    `за ${String(y.window_days)} сут: найдено ${String(y.items_found)} → ${articles} → ` +
    `материалов ${String(y.materials)}, коротких ${String(y.too_short)}`
  );
}

/** «robots.txt» в причине из реестра: включение — обход вопреки запрету сайта. */
const mentionsRobots = (note: string | null): boolean => note !== null && /robots/i.test(note);

/**
 * Новее ли строка экрана, чем база панели. ISO одной формы сравнивается строкой; `null`
 * (руками не меняли) — старше любого момента.
 */
function newerThan(next: SourceControl, base: SourceControl): boolean {
  const a = next.override.changed_at;
  const b = base.override.changed_at;
  if (a !== b) return b === null || (a !== null && a > b);
  return (
    next.weight !== base.weight ||
    next.enabled !== base.enabled ||
    next.registry.weight !== base.registry.weight ||
    next.registry.enabled !== base.registry.enabled ||
    next.registry.note !== base.registry.note
  );
}

export interface SourcePanelProps {
  readonly brand: string;
  readonly source: SourceControl;
  /** Подписано словом состояние здоровья — словарь живёт у экрана. */
  readonly stateLabel: string;
  /** Подробность здоровья (попытки, курсор, ошибка, журнал) — её рисует экран. */
  readonly health: ReactNode;
  readonly onSaved: (row: SourceControl) => void;
  readonly onClose: () => void;
}

export function SourcePanel({
  brand,
  source,
  stateLabel,
  health,
  onSaved,
  onClose,
}: SourcePanelProps): JSX.Element {
  const weightId = useId();
  // Строка, от которой считается «изменено»: сначала пришедшая с экрана, после сохранения —
  // из ответа PATCH (экран перечитывает список параллельно и может прийти позже).
  const [base, setBase] = useState<SourceControl>(source);
  const [weightChoice, setWeightChoice] = useState<number | null>(source.override.weight);
  const [enabledChoice, setEnabledChoice] = useState<boolean | null>(source.override.enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [conflict, setConflict] = useState(false);

  const weightDirty = weightChoice !== base.override.weight;
  const enabledDirty = enabledChoice !== base.override.enabled;
  const dirty = weightDirty || enabledDirty;
  const blocked = busy || !dirty;

  const adopt = useCallback((row: SourceControl): void => {
    setBase(row);
    setWeightChoice(row.override.weight);
    setEnabledChoice(row.override.enabled);
  }, []);

  // Перечитанный экраном список — новая база, пока несохранённого нет (см. шапку).
  useEffect(() => {
    if (!dirty && !busy && newerThan(source, base)) adopt(source);
  }, [source, base, dirty, busy, adopt]);

  const shownWeight = weightChoice ?? base.registry.weight;
  const shownEnabled = enabledChoice ?? base.registry.enabled;
  const registryOffTurnedOn = !base.registry.enabled && shownEnabled;

  /** Конфликт: перечитать строку и встать на неё; экран перечитает свой список. */
  const reload = useCallback(async (): Promise<void> => {
    const raw = await get<unknown>(`/api/v1/sources?brand=${encodeURIComponent(brand)}`);
    const parsed = SourceControlsResponseSchema.safeParse(raw);
    const row = parsed.success
      ? parsed.data.sources.find((r) => r.source_code === base.source_code)
      : undefined;
    if (row === undefined) throw new Error('строка источника не перечиталась');
    adopt(row);
    onSaved(row);
  }, [adopt, base.source_code, brand, onSaved]);

  const save = useCallback((): void => {
    const body: SourceOverridePatchBody = {
      ...(weightDirty ? { weight: weightChoice } : {}),
      ...(enabledDirty ? { enabled: enabledChoice } : {}),
      expected_changed_at: base.override.changed_at,
    };
    setBusy(true);
    setError(null);
    setSaved(false);
    setConflict(false);
    patch<unknown>(
      `/api/v1/sources/${encodeURIComponent(base.source_code)}?brand=${encodeURIComponent(brand)}`,
      body,
    )
      .then((raw) => {
        const parsed = SourceControlSchema.safeParse(raw);
        // Правка записана в любом случае: непонятный ответ — повод перечитать, а не «не вышло».
        if (parsed.success) {
          adopt(parsed.data);
          onSaved(parsed.data);
        } else {
          onSaved(base);
        }
        setSaved(true);
      })
      .catch(async (e: unknown) => {
        if (e instanceof ApiRequestError && e.code === 'source_override_conflict') {
          try {
            await reload();
            setConflict(true);
          } catch (again: unknown) {
            setError(`источник успели изменить, а перечитать не вышло: ${errorText(again)}`);
          }
          return;
        }
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  }, [adopt, base, brand, enabledChoice, enabledDirty, onSaved, reload, weightChoice, weightDirty]);

  const changed = base.override.changed_at;

  return (
    <Modal
      title={base.title ?? base.source_code}
      subtitle={base.source_code}
      width={640}
      onClose={onClose}
    >
      <div className={styles['head']}>
        {base.home_url === null ? null : (
          <a
            className={styles['home']}
            href={base.home_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {base.home_url}
          </a>
        )}
        <span className="meta" data-testid="source-saved">
          {stateLabel} · {base.enabled ? 'обходится' : 'не обходится'} · вес{' '}
          {weightLabel(base.weight)}
          {dirty ? ' · есть несохранённые изменения' : ''}
        </span>
      </div>

      <fieldset className={styles['field']}>
        <legend className="lbl">Вес в ранжировании</legend>
        <div className={styles['row']}>
          <input
            id={weightId}
            className={styles['range']}
            type="range"
            min={SOURCE_WEIGHT_MIN}
            max={SOURCE_WEIGHT_MAX}
            step={SOURCE_WEIGHT_STEP}
            value={shownWeight}
            aria-valuetext={weightLabel(shownWeight)}
            aria-label="Вес источника"
            disabled={busy}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              if (!Number.isFinite(v)) return;
              const w = canonicalSourceWeight(v);
              // Вернули на реестровое — это снятие отклонения, как у кнопок обхода.
              setWeightChoice(w === canonicalSourceWeight(base.registry.weight) ? null : w);
            }}
          />
          <output className={styles['value']} htmlFor={weightId} data-testid="source-weight">
            {weightLabel(shownWeight)}
          </output>
        </div>
        <div className={styles['row']}>
          <span className="meta">
            реестр {weightLabel(base.registry.weight)}
            {weightChoice === null ? ' · действует реестр' : ' · отклонение'}
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className={
              weightChoice === null || busy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'
            }
            disabled={weightChoice === null || busy}
            onClick={() => {
              setWeightChoice(null);
            }}
          >
            Сбросить вес к реестру
          </button>
        </div>
      </fieldset>

      <fieldset className={styles['field']}>
        <legend className="lbl">Обход</legend>
        <div className={styles['row']}>
          <div className={styles['chips']}>
            {[true, false].map((on) => (
              <button
                key={String(on)}
                type="button"
                className={shownEnabled === on ? 'fchip on' : 'fchip'}
                aria-pressed={shownEnabled === on}
                // Заглушку включить нельзя: бэкенд ответил бы 422 placeholder_source.
                disabled={busy || (on && base.placeholder && !shownEnabled)}
                onClick={() => {
                  // Совпало с реестром — это снятие отклонения, а не закрепление числа.
                  setEnabledChoice(on === base.registry.enabled ? null : on);
                }}
              >
                {on ? 'Включён' : 'Выключен'}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className={
              enabledChoice === null || busy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'
            }
            disabled={enabledChoice === null || busy}
            onClick={() => {
              setEnabledChoice(null);
            }}
          >
            Сбросить обход к реестру
          </button>
        </div>
        <span className="meta">
          реестр: {base.registry.enabled ? 'включён' : 'выключен'}
          {enabledChoice === null ? ' · действует реестр' : ' · отклонение'}
        </span>
        {base.placeholder ? (
          <div className="val val-info" data-testid="placeholder">
            <span className={styles['note']}>
              Строка-заглушка реестра: разметка листинга не снята — без неё обход собрал бы каждую
              ссылку страницы. Включить её можно только правкой реестра после замера.
            </span>
          </div>
        ) : null}
        {base.registry.enabled ? null : (
          <div
            className={registryOffTurnedOn ? 'val val-warn' : 'val val-info'}
            data-testid="registry-off"
          >
            <span className={styles['note']}>
              {registryOffTurnedOn
                ? 'В реестре этот источник выключен — включение отсюда действует поверх реестра.'
                : 'В реестре этот источник выключен.'}
              {base.registry.note === null ? null : (
                <>
                  {' '}
                  Причина из реестра: <q>{base.registry.note}</q>
                </>
              )}
              {registryOffTurnedOn && mentionsRobots(base.registry.note) ? (
                <>
                  {' '}
                  <strong data-testid="robots-warning">
                    Реестр выключил источник из-за robots.txt — включение означает обход вопреки
                    запрету.
                  </strong>
                </>
              ) : null}
            </span>
          </div>
        )}
      </fieldset>

      <span className="micro" data-testid="source-changed">
        {changed === null
          ? 'Руками не меняли — действует реестр.'
          : `изменил ${base.override.changed_by_login ?? 'неизвестно кто'}, ${shortMsk(changed)}`}
        {' · '}действует со следующего прогона мониторинга
      </span>

      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>Не сохранилось: {error}</span>
        </div>
      )}
      {conflict ? (
        <div className="val val-warn" role="status">
          <span>Источник успели изменить — данные обновлены. Проверьте и сохраните ещё раз.</span>
        </div>
      ) : null}
      {saved && error === null && !dirty ? (
        <div className="val val-info" role="status">
          <span>Сохранено.</span>
        </div>
      ) : null}

      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Закрыть
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={save}
        >
          Сохранить
        </button>
      </div>

      <div className={styles['health']}>
        <div className="lbl">Здоровье</div>
        <span className="meta" data-testid="source-yield">
          {yieldText(base.yield)}
        </span>
        {health}
      </div>
    </Modal>
  );
}
